import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FormControl, FormHelperText, FormLabel } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import { useEffect, useId, useState } from "react";
import { Controller, get, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PickerActionBar } from "./PickerActionBar";
/* the field value is a 24-hour `HH:mm` string; the picker works on a Dayjs anchored to today */ const toDayjs = (value)=>{
    const match = /^(\d{2}):(\d{2})$/.exec(String(value ?? ''));
    if (!match) return null;
    const parsed = dayjs().hour(+match[1]).minute(+match[2]).second(0);
    return parsed.isValid() ? parsed : null;
};
const toHHmm = (value)=>value?.isValid() ? value.format('HH:mm') : null;
export const TimeInput = (props)=>{
    const labelId = useId();
    const errorId = useId();
    const formContext = useFormContext();
    const translation = useTranslation();
    const fieldValue = useWatch({
        control: formContext.control,
        name: props.name
    });
    const [value, setValue] = useState(()=>toDayjs(fieldValue));
    const error = get(formContext.formState.errors, props.name);
    const testId = props['data-testid'] ?? props.name;
    useEffect(()=>{
        const externalValue = toDayjs(fieldValue);
        if (!externalValue) {
            if (value) setValue(null);
            return;
        }
        if (toHHmm(externalValue) !== toHHmm(value)) {
            setValue(externalValue);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        fieldValue
    ]);
    return /*#__PURE__*/ _jsx(LocalizationProvider, {
        dateAdapter: AdapterDayjs,
        children: /*#__PURE__*/ _jsx(Controller, {
            control: formContext.control,
            name: props.name,
            render: ({ field })=>/*#__PURE__*/ _jsxs(FormControl, {
                    error: !!error,
                    fullWidth: props.fullWidth,
                    required: props.required,
                    size: props.size,
                    children: [
                        /*#__PURE__*/ _jsx(FormLabel, {
                            disabled: props.disabled,
                            id: labelId,
                            required: props.required,
                            sx: props.hideLabel ? visuallyHidden : undefined,
                            children: props.label
                        }),
                        /*#__PURE__*/ _jsx(TimePicker, {
                            disabled: props.disabled,
                            onChange: (next)=>{
                                setValue(next);
                                /* a half-typed time is a non-null invalid Dayjs; leave the
                   committed field value alone so the reset-sync effect above
                   does not wipe what is still being typed */ if (!next || next.isValid()) {
                                    field.onChange(toHHmm(next));
                                }
                            },
                            slotProps: {
                                actionBar: {
                                    actions: [
                                        'accept'
                                    ],
                                    'data-testid': `clear-${testId}`
                                },
                                /* the action bar spans every layout column, so its width is shared into the empty first column and pushes the clock right; span the clock too so both start at the left edge */ layout: {
                                    sx: {
                                        '& .MuiPickersLayout-contentWrapper': {
                                            gridColumn: '1 / 4'
                                        }
                                    }
                                },
                                textField: {
                                    error: !!error,
                                    fullWidth: props.fullWidth,
                                    inputProps: {
                                        'aria-describedby': errorId,
                                        'aria-labelledby': labelId,
                                        'data-component': 'input',
                                        'data-testid': testId
                                    },
                                    required: props.required,
                                    size: props.size
                                }
                            },
                            slots: {
                                actionBar: PickerActionBar
                            },
                            value: value
                        }),
                        !props.hideError && /*#__PURE__*/ _jsx(FormHelperText, {
                            id: errorId,
                            children: translation.t(error?.message) || ' '
                        })
                    ]
                })
        })
    });
};
TimeInput.displayName = 'TimeInput';

//# sourceMappingURL=TimeInput.js.map